/**
 * Loading for the integration screens: values keyed by what they depend on,
 * with "still working" derived rather than stored, and a pause while a person
 * is still typing.
 */

import { useEffect, useState } from "react";

import { useAuth } from "@/spa/useAuth";
import { messageFor } from "@/spa/useApi";

/** Whether the person may change integrations. With sign-in off, anyone may. */
export function useCanWrite(): boolean {
  const auth = useAuth();
  if (auth.status !== "ready" || !auth.required) return true;
  return auth.account?.canWrite ?? false;
}

/** The value, once it has stopped changing for `ms`. */
export function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

export type Keyed<T> = {
  /** The newest value that loaded, even if a newer key is still loading. */
  data: T | undefined;
  /** The failure for the current key. */
  error: string | undefined;
  cause: unknown;
  /** The current key has not answered yet. */
  busy: boolean;
  reload: () => void;
};

/**
 * Load whenever `key` changes; `null` loads nothing.
 *
 * The last good value stays on screen while a new one loads, so a preview
 * does not blink empty on every keystroke.
 */
export function useKeyed<T>(key: string | null, load: () => Promise<T>, fallback = "could not load this"): Keyed<T> {
  const [nonce, setNonce] = useState(0);
  const full = key === null ? null : `${nonce}#${key}`;
  const [good, setGood] = useState<{ key: string; data: T } | null>(null);
  const [bad, setBad] = useState<{ key: string; error: string; cause: unknown } | null>(null);

  useEffect(() => {
    if (full === null) return;
    let cancelled = false;
    load().then(
      (data) => {
        if (!cancelled) setGood({ key: full, data });
      },
      (thrown: unknown) => {
        if (!cancelled) setBad({ key: full, error: messageFor(thrown, fallback), cause: thrown });
      },
    );
    return () => {
      cancelled = true;
    };
    // `load` belongs to the render that made `full`; `full` is what changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [full]);

  const failed = bad !== null && bad.key === full;
  return {
    data: good?.data,
    error: failed ? bad.error : undefined,
    cause: failed ? bad.cause : undefined,
    busy: full !== null && good?.key !== full && !failed,
    reload: () => setNonce((n) => n + 1),
  };
}

/** Read a value that lives in the browser only, safely. */
export function readLocal<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

export function writeLocal(key: string, value: unknown): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage blocked: the draft on the server still holds everything after step 2.
  }
}

/** The time, moving on every `ms`, so "4 min ago" stays true without reading the clock in render. */
export function useNow(ms = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(timer);
  }, [ms]);
  return now;
}

/** Run something every `ms` while `on`. */
export function useEvery(on: boolean, ms: number, tick: () => void): void {
  useEffect(() => {
    if (!on) return;
    const timer = setInterval(tick, ms);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on, ms]);
}
